import { useTransition } from '@/utils/cssTransitions';
import { ReturnComponent } from '@/utils/reactTypes';
import GlobalStyles from '@mui/material/GlobalStyles';
import { atom, useAtomValue, useSetAtom } from 'jotai';
import { useLayoutEffect } from 'react';

type RootNavOffset = { paddingLeft: string; paddingTop: string };

const NONE: RootNavOffset = { paddingLeft: '0px', paddingTop: '0px' };

// Single source of truth for the offset reserved on #root for the fixed nav
// drawer / app bar. Written by whichever drawer is mounted; read by the one
// persistent RootNavOffsetStyles below.
const rootNavOffsetAtom = atom<RootNavOffset>(NONE);

// Reserve a #root offset while the calling drawer is mounted. Writes on mount and
// value change, resets on unmount. A layout effect applies the value before paint,
// and, within a single route-transition commit, the incoming drawer's write (layout
// phase) lands after the outgoing drawer's cleanup (mutation phase) — so the offset
// never dips to zero when navigating between two drawer pages.
export const useReserveRootNavOffset = (offset: Partial<RootNavOffset>): void => {
    const setOffset = useSetAtom(rootNavOffsetAtom);
    const paddingLeft = offset.paddingLeft ?? '0px';
    const paddingTop = offset.paddingTop ?? '0px';
    useLayoutEffect(() => {
        setOffset({ paddingLeft, paddingTop });
        return () => setOffset(NONE);
    }, [setOffset, paddingLeft, paddingTop]);
};

// Mounted exactly once at the app root (see App.tsx). Because it never remounts across
// navigations, there is never a second identical #root <GlobalStyles> for emotion to
// adopt then flush during a concurrent route transition — the source of the intermittent
// post-login layout shift.
export const RootNavOffsetStyles = (): ReturnComponent => {
    const { paddingLeft, paddingTop } = useAtomValue(rootNavOffsetAtom);
    const transition = useTransition(['padding-left', 'padding-top']);
    return <GlobalStyles styles={{ '#root': { paddingLeft, paddingTop, transition } }} />;
};
